/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import T from 'i18n-react';
import type { NextPage, NextPageContext } from 'next';
import type { ParsedUrlQuery } from 'querystring';

import MetaTags from 'Components/MetaTags/MetaTags';
import PageComposer from 'Components/PageComposer/PageComposer';
import SignIn from 'Components/SignIn/SignIn';
import texts from 'Components/texts.json';
import { PAGE_SLUGS } from 'Utilities/consts';
import type { GetInitialPropsMiddlewareContext } from 'Utilities/getInitialPropsMiddlewares/getInitialPropsMiddlewares.types';
import { unauthorisedOnly } from 'Utilities/getInitialPropsMiddlewares/unauthorisedOnly/unauthorisedOnly';
import { createMiddlewarePipeline } from 'Utilities/helpers/middlewarePipeline/middlewarePipeline';
import { getVehiclePageMeta } from 'Utilities/vehiclePageMeta';

T.setTexts(texts);

type SigninPageProps = {
	asPath?: string;
	meta?: {
		description: string;
		height: number;
		image?: string;
		title: string;
		width: number;
	};
	query: ParsedUrlQuery;
};

const SigninPage: NextPage<SigninPageProps> = ({ asPath, meta, query }) => {
	return (
		<>
			<MetaTags
				// @ts-ignore
				includeCanonicalTag
				description={meta?.description || T.translate('metaTags.pages.signIn.description')}
				height={meta?.height}
				image={meta?.image}
				ogTitle={meta?.title || T.translate('metaTags.pages.signIn.title')}
				title={meta?.title || T.translate('metaTags.pages.signIn.title')}
				width={meta?.width}
			/>

			<PageComposer pageSlug={PAGE_SLUGS.SIGN_IN}>
				<SignIn asPath={asPath} query={query} />
			</PageComposer>
		</>
	);
};

export const signInGetInitialProps = async (context: NextPageContext): Promise<SigninPageProps> => {
	// @ts-ignore
	const { asPath, isSSR, query } = context;

	type MiddlewarePipelineContext = GetInitialPropsMiddlewareContext<typeof context, SigninPageProps>;

	const middlewarePipeline = createMiddlewarePipeline<MiddlewarePipelineContext>(unauthorisedOnly);

	const getInitialPropsMiddlewareContext: MiddlewarePipelineContext = {
		nextContext: context,
		returnValue: { asPath, query },
	};

	await middlewarePipeline.run(getInitialPropsMiddlewareContext);
	const meta = isSSR ? await getVehiclePageMeta(query.redirect) : {};

	return {
		...getInitialPropsMiddlewareContext.returnValue,
		...meta,
	};
};

SigninPage.getInitialProps = signInGetInitialProps;

export default SigninPage;
